import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Send, Loader2, Lock } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function Footer() {
  const navigate = useNavigate();
  const [contactForm, setContactForm] = useState({ email: '', message: '' });
  const [sending, setSending] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  useEffect(() => {
    checkUser();
  }, []);

  const checkUser = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/auth/me`, {
        credentials: 'include'
      });
      if (res.ok) {
        const userData = await res.json();
        setIsLoggedIn(true);
        setContactForm(prev => ({ ...prev, email: userData.email || '' }));
      }
    } catch (error) {
      // User not logged in
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!contactForm.email || !contactForm.message) {
      toast.error('Prosím vyplňte email a správu');
      return;
    }
    
    setSending(true);
    
    try {
      const res = await fetch(`${BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(contactForm)
      });
      
      const data = await res.json();
      
      if (data.success) {
        toast.success('Správa bola odoslaná. Ďakujeme!');
        setContactForm(prev => ({ ...prev, message: '' }));
      } else {
        throw new Error(data.detail || 'Failed to send');
      }
    } catch (error) {
      toast.error('Nepodarilo sa odoslať správu. Skúste to znova.');
    } finally {
      setSending(false);
    }
  };

  return (
    <footer data-testid="main-footer" className="bg-slate-900 text-slate-200 py-12 mt-auto">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div>
            <h3 className="text-white font-semibold mb-3" style={{fontFamily: 'Manrope, sans-serif'}}>O nás</h3>
            <p className="text-sm text-slate-400">
              EVMarket je platforma pre predaj a kúpu elektrických vozidiel. 
              Pripojte sa k zelenej revolúcii v doprave.
            </p>
          </div>
          <div>
            <h3 className="text-white font-semibold mb-3" style={{fontFamily: 'Manrope, sans-serif'}}>Kontaktujte nás</h3>
            {isLoggedIn ? (
              <form onSubmit={handleSubmit} className="space-y-3">
                <Input
                  data-testid="contact-email-input"
                  type="email"
                  placeholder="Váš email"
                  value={contactForm.email}
                  onChange={(e) => setContactForm({...contactForm, email: e.target.value})}
                  className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500 h-9 text-sm"
                  required
                />
                <Textarea
                  data-testid="contact-message-input"
                  placeholder="Vaša správa..."
                  value={contactForm.message}
                  onChange={(e) => setContactForm({...contactForm, message: e.target.value})}
                  className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500 text-sm resize-none"
                  rows={3}
                  required
                />
                <Button
                  data-testid="contact-submit-button"
                  type="submit"
                  disabled={sending}
                  className="w-full bg-green-600 hover:bg-green-700 text-white text-sm h-9"
                >
                  {sending ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Odosielam...
                    </>
                  ) : (
                    <>
                      <Send className="h-4 w-4 mr-2" />
                      Odoslať správu
                    </>
                  )}
                </Button>
              </form>
            ) : (
              <div className="text-center py-4">
                <Lock className="h-8 w-8 text-slate-500 mx-auto mb-3" />
                <p className="text-sm text-slate-400 mb-3">Kontaktovať môže len prihlásený užívateľ</p>
                <Button
                  data-testid="login-to-contact-button"
                  onClick={() => navigate('/login')}
                  variant="outline"
                  className="text-sm border-slate-600 text-slate-300 hover:bg-slate-800 hover:text-white"
                >
                  Prihlásiť sa
                </Button>
              </div>
            )}
          </div>
          <div>
            <h3 className="text-white font-semibold mb-3" style={{fontFamily: 'Manrope, sans-serif'}}>Právne informácie</h3>
            <Link to="/obchodne-podmienky" className="block text-sm text-slate-400 hover:text-white transition-colors">
              Obchodné podmienky
            </Link>
            <Link to="/ochrana-osobnych-udajov" className="block text-sm text-slate-400 hover:text-white transition-colors mt-2">
              Ochrana osobných údajov
            </Link>
          </div>
        </div>
        <div className="border-t border-slate-800 mt-8 pt-6 text-center text-sm text-slate-400">
          © 2026 EVMarket. Všetky práva vyhradené.
        </div>
      </div>
    </footer>
  );
}

export default Footer;
